import { useState } from 'react';

import { autoFormaliseOffers } from '@api/autoFormaliseOffers';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { IconCalendarOutline } from '@iwoca/icons';
import { Card, Spinner } from '@iwoca/orion';
import IwRedirect from '@iwRouter/IwRedirect';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FormattedDate, FormattedMessage } from 'react-intl';

import messages from './EndedView.messages';
import { getLastRepaymentDate } from './NewLoanEndedView.helpers';
import styles from './NewLoanEndedView.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import InternationalisedName from '../dashboardComponents/InternationalisedName';
import {
    CreateNewApplicationForm,
    TApplicationStatus
} from '../NewOutstandingView/CreateNewApplicationForm/CreateNewApplicationForm';

type TProps = { accountId: string };

const NewLoanEndedView = ({ accountId }: TProps) => {
    const stateKey = useStateKey();

    const {
        data: accountsResponse,
        isLoading,
        isError
    } = useQuery({
        ...getAccountsQueryConfig(stateKey)
    });

    if (isLoading || isError) return <Spinner />;

    const loanEndedAccount = accountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const { cashflows } = loanEndedAccount;
    const lastRepaymentDate = getLastRepaymentDate(cashflows!);

    return (
        <div className="col-span-full m-auto">
            <Card className={styles.loanSummaryHeader}>
                <h1
                    className={styles.welcomeTitle}
                    data-testid="new-loan-ended-view"
                >
                    <FormattedMessage
                        {...messages.newWelcomeBack}
                        values={{
                            name: <InternationalisedName />
                        }}
                    />
                </h1>
                <div className={styles.welcomeSubtitle}>
                    <IconCalendarOutline />
                    <p>
                        <FormattedMessage
                            {...messages.subtitle}
                            values={{
                                date: (
                                    <strong>
                                        {' '}
                                        <FormattedDate
                                            day="2-digit"
                                            month="long"
                                            year="numeric"
                                            value={new Date(lastRepaymentDate!)}
                                        />
                                    </strong>
                                )
                            }}
                        />
                    </p>
                </div>
            </Card>
            <Card className={styles.loanSummaryBody}>
                <h2 className={styles.loanSummaryTitle}>
                    <FormattedMessage {...messages.loanSummaryActionTitle} />
                </h2>
                <ul className={styles.loanSummaryItems}>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage {...messages.bulletpointOne} />
                    </li>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage {...messages.bulletpointTwo} />
                    </li>
                    <li className={styles.loanSummaryItem}>
                        <FormattedMessage {...messages.bulletpointThree} />
                    </li>
                </ul>
                <LoanEndedForm accountId={accountId} />
            </Card>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default NewLoanEndedView;

const LoanEndedForm = ({ accountId }: { accountId: string }) => {
    const [applicationStatus, setApplicationStatus] =
        useState<TApplicationStatus>(null);
    const stateKey = useStateKey();
    const queryClient = useQueryClient();

    const handleSuccess = async () => {
        const { result } = await autoFormaliseOffers({
            stateKey
        });

        switch (result) {
            case 'FORMALISED':
                await queryClient.invalidateQueries({
                    queryKey: ['accounts']
                });
                await queryClient.invalidateQueries({
                    queryKey: ['application', stateKey]
                });
                setApplicationStatus('auto_approved');
                break;
            case 'DECLINED':
                await queryClient.invalidateQueries({
                    queryKey: ['application', stateKey]
                });
                setApplicationStatus('auto_declined');
                break;
            case 'DOCS_COMPLETE':
                setApplicationStatus('docs_complete');
                break;
            case 'DOCS_REQUIRED':
                setApplicationStatus('requires_documents');
                break;
        }
    };

    switch (applicationStatus) {
        case 'requires_documents':
            return <IwRedirect pathname="/apply-flexi-loan/" />;

        case 'auto_approved':
        case 'auto_declined':
        case 'docs_complete':
            return <IwRedirect pathname="/my-offers/" />;

        default:
            return (
                <CreateNewApplicationForm
                    accountId={accountId}
                    setApplicationStatus={setApplicationStatus}
                    amountFieldTitle={
                        <FormattedMessage {...messages.amountInputTitle} />
                    }
                    turnoverFieldTitle={
                        <FormattedMessage {...messages.turnoverInputTitle} />
                    }
                    onSuccess={handleSuccess}
                />
            );
    }
};
